import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import { ModuleMapping } from '@/types';

export default function ModuleMappings() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<ModuleMapping | null>(null);
  const [mappingToDelete, setMappingToDelete] = useState<ModuleMapping | null>(null);
  
  const [jiraModule, setJiraModule] = useState('');
  const [productSuite, setProductSuite] = useState('');

  const { data: mappings, isLoading, error } = useQuery({
    queryKey: ['moduleMappings'],
    queryFn: () => api.getModuleMappings(),
  });

  const createMutation = useMutation({
    mutationFn: (mapping: Partial<ModuleMapping>) => api.createModuleMapping(mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleMappings'] });
      queryClient.invalidateQueries({ queryKey: ['productSuites'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (mapping: ModuleMapping) => api.updateModuleMapping(mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleMappings'] });
      queryClient.invalidateQueries({ queryKey: ['productSuites'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteModuleMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleMappings'] });
      queryClient.invalidateQueries({ queryKey: ['productSuites'] });
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
    },
  });

  const handleOpenDialog = (mapping?: ModuleMapping) => {
    if (mapping) {
      setEditingMapping(mapping);
      setJiraModule(mapping.jiraModule);
      setProductSuite(mapping.productSuite);
    } else {
      setEditingMapping(null);
      setJiraModule('');
      setProductSuite('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
    setJiraModule('');
    setProductSuite('');
  };

  const handleSave = () => {
    const mappingData = { jiraModule, productSuite };
    if (editingMapping) {
      updateMutation.mutate({ ...editingMapping, ...mappingData });
    } else {
      createMutation.mutate(mappingData);
    }
  };

  const handleDeleteClick = (mapping: ModuleMapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading module mappings: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Module Mappings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Mapping
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Module mappings link Jira modules to product suites for grouping in release notes.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Jira Module</TableCell>
              <TableCell>Product Suite</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(mappings) && mappings.length > 0 ? (
              mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>{mapping.jiraModule}</TableCell>
                  <TableCell>
                    <Chip
                      label={mapping.productSuite}
                      size="small"
                      sx={{ backgroundColor: '#E0F2F1', color: '#00695C' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(mapping)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(mapping)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No mappings configured</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMapping ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Jira Module"
              value={jiraModule}
              onChange={(e) => setJiraModule(e.target.value)}
              fullWidth
              helperText="The module name as it appears in Jira"
            />
            <TextField
              label="Product Suite"
              value={productSuite}
              onChange={(e) => setProductSuite(e.target.value)}
              fullWidth
              helperText="e.g., Fresh 360, Inventory 360, Commerce 360"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !jiraModule || !productSuite}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the mapping for <strong>{mappingToDelete?.jiraModule}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => mappingToDelete && deleteMutation.mutate(mappingToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
